import { IUserContact } from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement, useState } from 'react';

import { get } from '../../api/utils';
import CircleProfilePicture from '../common/CircleProfilePicture';
import CopyOnClick from '../common/CopyOnClick';
import DateTime from '../common/DateTime';
import ErrorAlert from '../common/ErrorAlert';
import Icon from '../common/Icon';
import Spinner from '../common/Spinner';
import Modal from '../common/modal/Modal';
import { t } from '../../utils/i18n';

export interface IProjectPeerReview {
    availableReviewersURI: string;
    canReviewPeerDirectly: boolean;
    correctCorrectionURI: string;
    qaReviewsURI: string;
    readyForReviewURI: string;
    reviewDeadline: string | null;
    synchronousManualReview: boolean;
}

interface IProps {
    csrfToken: string;
    peerReview: IProjectPeerReview;
}

export default function ProjectReadyForReviewBody({
    csrfToken,
    peerReview: {
        availableReviewersURI,
        canReviewPeerDirectly,
        correctCorrectionURI,
        reviewDeadline,
        qaReviewsURI,
        synchronousManualReview,
    },
}: IProps): ReactElement {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [availableReviewers, setAvailableReviewers] = useState<
        IUserContact[]
    >([]);

    const onLoadAvailableReviewers = async (): Promise<void> => {
        try {
            setError(null);
            setAvailableReviewers([]);
            setLoading(true);
            const reviewers = await get<IUserContact[]>(
                availableReviewersURI,
                csrfToken,
            );
            if (reviewers.length === 0) {
                setError('No peer is currently able to review your work.');
            } else {
                setAvailableReviewers(reviewers);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {!synchronousManualReview && (
                <p>
                    When you are ready to be reviewed, share your link to
                    your peers.
                </p>
            )}

            {synchronousManualReview && (
                <p>
                    {t('projects.review.connect_with_peers', 'Connect with')}{' '}
                    <a
                        data-toggle="modal"
                        href="#available-reviewers-modal"
                        onClick={onLoadAvailableReviewers}
                    >
                        {t('projects.review.one_of_your_peers', 'one of your peers')}
                    </a>{' '}
                    {t('projects.review.now_to_review', 'now to review your work together.')}
                </p>
            )}

            <Modal
                id="available-reviewers-modal"
                title={t('projects.review.contact_one_of_peers', 'Contact one of your peers')}
            >
                {loading && <Spinner />}

                {!loading && error && (
                    <div className="sm-gap">
                        <ErrorAlert error={error} />
                    </div>
                )}

                {availableReviewers.map((contact) => (
                    <div
                        className="align-items-center d-flex justify-content-between"
                        key={contact.user.id}
                    >
                        <CircleProfilePicture size={60} user={contact.user} />
                        <div className="flex-grow-1 ml-2">
                            {contact.user.full_name}
                        </div>
                        <div>
                            {contact.slack_direct_message_url && (
                                <a
                                    href={contact.slack_direct_message_url}
                                    rel="noreferrer"
                                    target="_blank"
                                    title="Slack"
                                >
                                    <Icon fixedWidth name="slack" />
                                </a>
                            )}
                            <a
                                href={`mailto:${contact.email}`}
                                rel="noreferrer"
                                target="_blank"
                                title={`Send email to ${contact.email}`}
                            >
                                <Icon fixedWidth name="envelope" />
                            </a>
                        </div>
                    </div>
                ))}
            </Modal>

            <div className="input-group d-block my-2">
                <span className="form-control user-select-all max-width-250-ellipsis">
                    {correctCorrectionURI}
                </span>
                <span className="input-group-btn">
                    <a className="btn btn-default">
                        <CopyOnClick copyText={correctCorrectionURI}>
                            <Icon fixedWidth name="clipboard" />
                        </CopyOnClick>
                    </a>
                </span>
            </div>

            {reviewDeadline && (
                <p>
                    <strong>
                        Reviews are due by <DateTime value={reviewDeadline} />
                    </strong>
                </p>
            )}
        </>
    );
}
